/** Cake base + sweetener + flavor — one colorful block on customer proof window. */
import { formatKitchenTierLabel } from "@/lib/kitchenTierLabel";

function KitchenTierBanner({ tiers, className = "" }) {
  const label = formatKitchenTierLabel(tiers);
  if (!label) return null;
  return (
    <div
      className={`rounded-xl border-2 border-violet-500 bg-violet-100 px-3 py-2 text-center ${className}`}
      data-testid="kitchen-tier-banner"
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-violet-900">Tier size</p>
      <p className="text-lg font-black leading-tight text-violet-950">{label}</p>
    </div>
  );
}

export function OrderBakingSpecDisplay({ info, variant = "default" }) {
  if (info?.is_cheesecake) return null;

  const lines = info?.baking_specs?.lines?.length
    ? info.baking_specs.lines
    : [
        {
          flavor: info?.flavor,
          base_flour: info?.base_flour || "Maida",
          sweetener: info?.sweetener || "Sugar",
        },
      ];

  const weightLabel =
    info?.weight_label && info.weight_label !== "-"
      ? info.weight_label
      : info?.weight_kg
        ? `${info.weight_kg} kg`
        : "";

  if (variant === "driver") {
    return (
      <div className="driver-card" style={{ padding: "12px 16px" }}>
        <div className="driver-label">Specific base & sweetener</div>
        <div className="space-y-2">
          {lines.map((line, i) => (
            <div key={`${line.flavor}-${i}`} className="driver-frost-line" style={{ flexWrap: "wrap" }}>
              {lines.length > 1 && line.flavor ? (
                <span className="driver-frost-chip driver-frost-chip--flavor">{line.flavor}</span>
              ) : null}
              <span className="driver-frost-chip" style={{ background: "#FEF3C7", color: "#92400E" }}>
                Base: {line.base_flour || "Maida"}
              </span>
              <span className="driver-frost-chip" style={{ background: "#E0F2FE", color: "#0369A1" }}>
                Sweetener: {line.sweetener || "Sugar"}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "customer") {
    const flavorName = lines[0]?.flavor || info?.flavor || "Cake";
    return (
      <div className="cp-info-card cp-order-summary-card">
        <div className="cp-info-card-label">Your cake at a glance</div>
        <div className="cp-order-chip-grid">
          <span className="cp-order-chip cp-order-chip--flavor">{flavorName}</span>
          <span className="cp-order-chip cp-order-chip--base">Base: {lines[0]?.base_flour || "Maida"}</span>
          <span className="cp-order-chip cp-order-chip--sweet">
            Sweetener: {lines[0]?.sweetener || "Sugar"}
          </span>
          {weightLabel ? (
            <span className="cp-order-chip cp-order-chip--weight">{weightLabel}</span>
          ) : null}
        </div>
      </div>
    );
  }

  if (lines.length === 1) {
    const line = lines[0];
    return (
      <div className="space-y-2">
        <KitchenTierBanner tiers={info?.tiers} />
        <div className="grid grid-cols-2 gap-2">
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 px-2.5 py-2">
          <p className="text-[10px] font-bold uppercase tracking-wide text-amber-800">Cake base</p>
          <p className="text-base font-extrabold text-[#2D2D2D]">{line.base_flour || "Maida"}</p>
        </div>
        <div className="rounded-xl border-2 border-sky-300 bg-sky-50 px-2.5 py-2">
          <p className="text-[10px] font-bold uppercase tracking-wide text-sky-800">Sweetener</p>
          <p className="text-base font-extrabold text-[#2D2D2D]">{line.sweetener || "Sugar"}</p>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <KitchenTierBanner tiers={info?.tiers} />
      {lines.map((line, i) => (
        <div key={`${line.flavor}-${i}`} className="rounded-xl border border-[#EBB3A9] bg-white px-2.5 py-2">
          {line.flavor ? (
            <p className="text-[10px] font-bold uppercase tracking-wide text-[#888]">{line.flavor}</p>
          ) : null}
          <div className="mt-1 grid grid-cols-2 gap-2">
            <p className="text-sm font-extrabold text-[#2D2D2D]">
              <span className="text-amber-800">Base:</span> {line.base_flour || "Maida"}
            </p>
            <p className="text-sm font-extrabold text-[#2D2D2D]">
              <span className="text-sky-800">Sweetener:</span> {line.sweetener || "Sugar"}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
